import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';
import { fetchDisbursements } from '~/services/campaign';
import { downloadCSV } from '~/utils/csv';
import {
    Disbursement,
    GroupedDisbursement,
    DisbursementsResponse,
} from '~/types/disbursements';

type SortDirection = 'asc' | 'desc';

const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const defaultTo = moment().format('YYYY-MM-DD');
const defaultFrom = moment(defaultTo).subtract(30, 'days').format('YYYY-MM-DD');

const DisbursementsTab: React.FC<{ campaign: any }> = () => {
    const { campaign_id } = useParams();
    const [from, setFrom] = useState<string>(defaultFrom);
    const [to, setTo] = useState<string>(defaultTo);
    const [loading, setLoading] = useState<boolean>(true);
    const [filtering, setFiltering] = useState<boolean>(false);
    const [groups, setGroups] = useState<GroupedDisbursement[]>([]);
    const [sortDir, setSortDir] = useState<SortDirection>('asc');

    const buildGroups = (resp: DisbursementsResponse): GroupedDisbursement[] => {
        const byVendor = Map.groupBy(
            resp.disbursements,
            (d: Disbursement) => d.vendor_id
        );
        return Object.entries(resp.totals).map(([vendor_id, total]) => ({
            ...total,
            disbursements: byVendor.get(vendor_id) || [],
        })) as GroupedDisbursement[];
    };

    const load = async (
        startDate: string | null,
        endDate: string | null,
        isFilter = false
    ) => {
        if (!campaign_id) return;
        isFilter ? setFiltering(true) : setLoading(true);
        try {
            const resp = await fetchDisbursements(
                campaign_id,
                startDate,
                endDate
            );
            setGroups(buildGroups(resp));
        } catch (e) {
            console.error('Error fetching disbursements:', e);
            toast.error('Failed to load disbursements');
        } finally {
            isFilter ? setFiltering(false) : setLoading(false);
        }
    };

    useEffect(() => {
        load(defaultFrom, defaultTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign_id]);

    const sortedGroups = useMemo(() => {
        const copy = [...groups];
        copy.sort((a, b) => {
            const cmp = a.name.localeCompare(b.name, undefined, {
                sensitivity: 'base',
            });
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [groups, sortDir]);

    const grandTotal = useMemo(
        () => groups.reduce((sum, g) => sum + g.amount, 0),
        [groups]
    );

    const toggleVendorSort = () =>
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));

    const handleExport = () => {
        const rows = sortedGroups.flatMap((group) =>
            group.disbursements.map((d) => ({
                'Check #': d.doc_number,
                Vendor: group.name,
                'Payee Address':
                    d.check_address != null ? d.check_address : group.address,
                Date: moment(d.effective_date).format('L'),
                Amount: d.amount,
            }))
        );
        downloadCSV(rows, 'Disbursements');
    };

    return (
        <div className='p-4'>
            <div className='mb-4 flex items-center justify-between'>
                <h2 className='text-2xl font-bold text-[#131212]'>
                    Disbursements
                </h2>
                <button
                    type='button'
                    className='rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                    onClick={handleExport}
                    disabled={loading || groups.length === 0}
                >
                    Export List
                </button>
            </div>

            <div className='mb-6 flex flex-wrap items-center gap-3 rounded-lg bg-[#F47D29]/[.1] p-4'>
                <span className='text-[#131212]'>Show contributions from</span>
                <input
                    type='date'
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className='rounded-md border border-black bg-white px-3 py-2'
                />
                <span className='text-[#131212]'>to</span>
                <input
                    type='date'
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    className='rounded-md border border-black bg-white px-3 py-2'
                />
                <button
                    type='button'
                    className='rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                    onClick={() => load(from, to, true)}
                    disabled={filtering}
                >
                    {filtering ? 'Loading…' : 'Filter'}
                </button>
            </div>

            {loading ? (
                <p className='py-8 text-center text-[#637381]'>
                    Loading disbursements…
                </p>
            ) : (
                <table className='min-w-full bg-white'>
                    <thead>
                        <tr className='border-b border-[#E5E7EB] text-left'>
                            <th className='px-2 py-3'>Check #</th>
                            <th className='px-2 py-3'>
                                <button
                                    type='button'
                                    onClick={toggleVendorSort}
                                    className='inline-flex items-center gap-1 font-bold'
                                >
                                    Vendor
                                    <span className='text-xs'>
                                        {sortDir === 'asc' ? '▲' : '▼'}
                                    </span>
                                </button>
                            </th>
                            <th className='px-2 py-3'>Payee Address</th>
                            <th className='px-2 py-3'>Date</th>
                            <th className='px-2 py-3 text-right'>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedGroups.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className='py-8 text-center text-[#637381]'
                                >
                                    No disbursements found for this date range.
                                </td>
                            </tr>
                        ) : (
                            sortedGroups.map((group, index) => (
                                <DisbursementGroup key={index} group={group} />
                            ))
                        )}
                        {sortedGroups.length > 0 && (
                            <tr className='border-t-2 border-[#131212]'>
                                <td className='px-2 py-3' colSpan={3} />
                                <td className='px-2 py-3 font-bold'>
                                    Total Requested
                                </td>
                                <td className='px-2 py-3 text-right font-bold'>
                                    {formatCurrency(grandTotal)}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const DisbursementGroup: React.FC<{ group: GroupedDisbursement }> = ({
    group,
}) => (
    <>
        <tr>
            <td colSpan={5} className='px-2 pb-2 pt-5'>
                <span className='text-base font-bold uppercase text-[#131212]'>
                    {group.name}
                </span>
            </td>
        </tr>
        {group.disbursements.map((item, subIndex) => (
            <tr key={subIndex} className='border-b border-[#E5E7EB]'>
                <td className='px-2 py-3'>{item.doc_number}</td>
                <td className='px-2 py-3'>{group.name}</td>
                <td className='px-2 py-3'>
                    {item.check_address != null
                        ? item.check_address
                        : group.address}
                </td>
                <td className='px-2 py-3'>
                    {moment(item.effective_date).format('L')}
                </td>
                <td className='px-2 py-3 text-right'>
                    {formatCurrency(parseFloat(item.amount))}
                </td>
            </tr>
        ))}
        <tr className='border-b border-[#E5E7EB]'>
            <td className='px-2 py-3' colSpan={3} />
            <td className='px-2 py-3 font-bold'>Total</td>
            <td className='px-2 py-3 text-right font-bold'>
                {formatCurrency(group.amount)}
            </td>
        </tr>
    </>
);

export default DisbursementsTab;
